import { useState, useEffect, useRef, createContext, useContext } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Timeout for role check to prevent infinite loading
const ROLE_CHECK_TIMEOUT_MS = 10000;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const roleCheckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (roleCheckTimeoutRef.current) {
        clearTimeout(roleCheckTimeoutRef.current);
      }
    };
  }, []);

  // SYNCHRONOUS auth state listener - no async/await here!
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // Only synchronous state updates
        setSession(session);
        setUser(session?.user ?? null);
        
        // If logged out, immediately set admin false and stop loading
        if (!session?.user) {
          setIsAdmin(false);
          setIsLoading(false);
        }
      }
    );

    // Initial session check
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (isMountedRef.current) {
        setSession(session);
        setUser(session?.user ?? null);
        if (!session?.user) {
          setIsLoading(false);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Separate effect for admin role check - triggered by user changes
  useEffect(() => {
    if (!user?.id) {
      setIsAdmin(false);
      return;
    }

    // Start loading for role check
    setIsLoading(true);

    // Set up timeout watchdog to prevent infinite loading
    roleCheckTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        console.warn('Admin role check timed out after', ROLE_CHECK_TIMEOUT_MS, 'ms');
        setIsAdmin(false);
        setIsLoading(false);
      }
    }, ROLE_CHECK_TIMEOUT_MS);

    // Perform the role check
    const checkRole = async () => {
      try {
        const { data, error } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
          .eq('role', 'admin')
          .maybeSingle();

        if (isMountedRef.current) {
          if (error) {
            console.error('Error checking admin role:', error);
            setIsAdmin(false);
          } else {
            setIsAdmin(!!data);
          }
        }
      } catch (err) {
        console.error('Error checking admin role:', err);
        if (isMountedRef.current) {
          setIsAdmin(false);
        }
      } finally {
        // Clear timeout and finish loading
        if (roleCheckTimeoutRef.current) {
          clearTimeout(roleCheckTimeoutRef.current);
          roleCheckTimeoutRef.current = null;
        }
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    checkRole();

    return () => {
      if (roleCheckTimeoutRef.current) {
        clearTimeout(roleCheckTimeoutRef.current);
        roleCheckTimeoutRef.current = null;
      }
    };
  }, [user?.id]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/`;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
      },
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setIsAdmin(false);
  };

  return (
    <AuthContext.Provider value={{ user, session, isAdmin, isLoading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
